/**
 * Cliente HTTP central do front-delivai (DelivAI v2).
 *
 * Centraliza:
 *   - base URL (backend Kotlin via REACT_APP_BACKEND_URL ou proxy /api/n8n em dev)
 *   - SEC-2: sempre `credentials: 'include'` (cookie HttpOnly); header legado
 *     `REACT_APP_N8N_AUTH_HEADER` só se ainda estiver no env (dual auth).
 *   - tratamento padronizado de erros (ApiClientError)
 *   - timeout via AbortController
 *   - propagação do X-Correlation-Id
 *
 * Uso (services novos):
 *   import { apiClient } from './api/client';
 *   const orders = await apiClient.get<Order[]>('/webhook/get-orders', { params: { cnpj } });
 */
import { N8N_AUTH_HEADER, N8N_WEBHOOK_URL, BACKEND_URL } from "../../config";
import { handlePanelUnauthorized } from "../../utils/sessionExpired";
import { ApiClientError, parseApiErrorPayload } from "./errors";
import { fetchWithTransientRetry, isGatewayStatus } from "./transientRetry";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type ApiClientOptions = {
  method?: HttpMethod;
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  timeoutMs?: number;
  /**
   * Se true, omite o header legado mesmo se `REACT_APP_N8N_AUTH_HEADER` existir
   * (login/me/logout — só cookie). Credentials include sempre.
   */
  cookieAuth?: boolean;
  /** Se true, não anexa Authorization legado — endpoints públicos de onboarding. */
  skipAuth?: boolean;
};

const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Base para rotas `/api/*` (Kotlin). Dev: proxy CRA. Prod: mesmo host (nginx tubo).
 * BACKEND_URL absoluto no browser não recebe o cookie HttpOnly (outro site).
 */
function kotlinApiBase(pathOrUrl: string): string {
  if (process.env.NODE_ENV !== "development") return "";
  if (pathOrUrl.startsWith("/api/google")) {
    return "";
  }
  if (BACKEND_URL) return BACKEND_URL.replace(/\/$/, "");
  return (N8N_WEBHOOK_URL || "").replace(/\/webhook\/?$/, "").replace(/\/$/, "");
}

function buildUrl(
  pathOrUrl: string,
  params?: ApiClientOptions["params"]
): string {
  const useKotlinApi = pathOrUrl.startsWith("/api/");
  const base = useKotlinApi ? kotlinApiBase(pathOrUrl) : (N8N_WEBHOOK_URL || "");
  let url: string;
  if (/^https?:\/\//i.test(pathOrUrl)) {
    url = pathOrUrl;
  } else if (pathOrUrl.startsWith("/")) {
    const trimmedBase = base.replace(/\/webhook\/?$/, "").replace(/\/$/, "");
    let path = pathOrUrl;
    // Dev: base = /api/n8n e path = /webhook/... → proxy já prefixa /webhook (evita /webhook/webhook/...).
    if (
      path.startsWith("/webhook/") &&
      (trimmedBase === "/api/n8n" || trimmedBase.endsWith("/api/n8n"))
    ) {
      path = path.slice("/webhook".length);
    }
    url = `${trimmedBase}${path}`;
  } else {
    const trimmedBase = base.replace(/\/$/, "");
    url = `${trimmedBase}/${pathOrUrl}`;
  }
  if (params) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") {
        qs.append(k, String(v));
      }
    });
    const query = qs.toString();
    if (query) {
      url += url.includes("?") ? `&${query}` : `?${query}`;
    }
  }
  return url;
}

function generateCorrelationId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return `cid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

async function parseResponseBody(res: Response): Promise<unknown> {
  const ct = res.headers.get("content-type") || "";
  if (res.status === 204) return null;
  if (ct.includes("application/json")) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  try {
    return await res.text();
  } catch {
    return null;
  }
}

async function request<T>(
  pathOrUrl: string,
  options: ApiClientOptions = {}
): Promise<T> {
  const method = options.method || "GET";
  const url = buildUrl(pathOrUrl, options.params);
  const correlationId = generateCorrelationId();
  const timeout = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Correlation-Id": correlationId,
    ...(options.headers || {}),
  };
  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
  }
  // Dual SEC-2: cookie sempre; header só se ainda houver no bundle e não for cookieAuth puro.
  if (!options.skipAuth && !options.cookieAuth && N8N_AUTH_HEADER) {
    headers["Authorization"] = N8N_AUTH_HEADER;
  }

  const controller = new AbortController();
  const externalSignal = options.signal;
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener("abort", () => controller.abort());
  }
  let timedOut = false;
  const timeoutHandle = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeout);

  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    body =
      options.body instanceof FormData
        ? options.body
        : JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetchWithTransientRetry(url, {
      method,
      headers,
      body,
      // same-origin cobre /api/n8n; include cobre front/API em hosts distintos.
      credentials: "include",
      signal: controller.signal,
    });
  } catch (err: unknown) {
    clearTimeout(timeoutHandle);
    if ((err as { name?: string })?.name === "AbortError") {
      if (externalSignal?.aborted && !timedOut) {
        throw new ApiClientError({
          code: "ABORTED",
          message: "Request aborted",
          status: 0,
          correlationId,
        });
      }
      throw new ApiClientError({
        code: "NETWORK_TIMEOUT",
        message: `Timeout (${timeout}ms) ao chamar ${method} ${pathOrUrl}`,
        status: 0,
        correlationId,
      });
    }
    const network = new ApiClientError({
      code: "NETWORK_ERROR",
      message: (err as Error)?.message || "Falha de rede",
      status: 0,
      correlationId,
    });
    network.retriesExhausted = true;
    throw network;
  } finally {
    clearTimeout(timeoutHandle);
  }

  const payload = await parseResponseBody(response);

  if (!response.ok) {
    const parsed = parseApiErrorPayload(payload, response.status);
    if (response.status === 401) {
      handlePanelUnauthorized(url);
    }
    const apiError = new ApiClientError({
      ...parsed,
      status: response.status,
      correlationId:
        response.headers.get("X-Correlation-Id") || correlationId,
    });
    if (isGatewayStatus(response.status) || response.status === 429) {
      apiError.retriesExhausted = true;
    }
    throw apiError;
  }

  return (payload as T) ?? (undefined as T);
}

export const apiClient = {
  request,
  get: <T>(p: string, o?: Omit<ApiClientOptions, "method" | "body">) =>
    request<T>(p, { ...o, method: "GET" }),
  post: <T>(p: string, body?: unknown, o?: Omit<ApiClientOptions, "method">) =>
    request<T>(p, { ...o, method: "POST", body }),
  put: <T>(p: string, body?: unknown, o?: Omit<ApiClientOptions, "method">) =>
    request<T>(p, { ...o, method: "PUT", body }),
  patch: <T>(p: string, body?: unknown, o?: Omit<ApiClientOptions, "method">) =>
    request<T>(p, { ...o, method: "PATCH", body }),
  delete: <T>(p: string, o?: Omit<ApiClientOptions, "method" | "body">) =>
    request<T>(p, { ...o, method: "DELETE" }),
};
